import { Api } from "../../service/Api";
import { useCallback, useMemo } from "react";
import { OrganizationPublicId } from "../../service/server/Types";
import { UseApiData } from "../api";
import { useDataRequest } from "../api";
import { OrganizationSurveyDto } from "../../service/server/dto/org/OrganizationSurveyDto";
import {
  useIsMutating,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { PatchOrganizationSurveyAnswerRequest } from "../../service/server/dto/org/OrganizationSurveyRequestDto";
import { updateQueryData, useApiSnackbar } from "../../service/resources/mutators";
import * as Mutators from "../../service/resources/org/surveyMutators";
import {
  emptyOrgSurvey,
  orgSurveyApplyLocalPending,
} from "../../service/resources/org/orgSurveyHelpers";
import { safeJsonParse } from "../../../polaris/surveys/util";
import _ from "lodash";

export type SurveyAnswers = Record<string, string | undefined>;
export type SurveyResponses = SurveyAnswers;
export type ParsedSurveyAnswers = Record<string, boolean | null>;
export type SurveyAnswerFn = (
  slug: string,
  answer: string,
  question?: string,
  flaggedForReview?: boolean,
) => Promise<SurveyAnswers | void>;

export type UseSurvey = UseApiData<OrganizationSurveyDto> & {
  answers: SurveyAnswers;
  setAnswer: SurveyAnswerFn;
  mutators: {
    patchAnswer: SurveyPatchAnswerMutation;
  };
  flaggedForReviewSlugs: string[];
};

export const useSurvey = (
  orgId: OrganizationPublicId,
  surveyName: string,
  onAnswer?: () => Promise<void>,
): UseSurvey => {
  const api = useCallback(
    () => Api.orgSurvey.getAnswersForSurvey(orgId, surveyName),
    [orgId, surveyName],
  );

  const { result, refresh, request, invalidate } = useDataRequest({
    api,
    queryKey: queryKeySurvey(orgId, surveyName),
  });

  // make mutations available
  const patchAnswer = usePatchSurveyAnswer(orgId, surveyName);
  const setAnswer: SurveyAnswerFn = useCallback(
    async (
      slug: string,
      answer: string,
      question?: string,
      flaggedForReview?: boolean,
    ): Promise<SurveyAnswers> => {
      let result = await patchAnswer.mutateAsync([{ slug, answer, question, flaggedForReview }]);
      if (onAnswer) {
        await onAnswer();
      }
      return result && result.answers
        ? Object.fromEntries(Object.values(result.answers).map((q) => [q.slug, q.answer]))
        : {};
    },
    [orgId, surveyName, patchAnswer],
  );

  // apply local mutations
  const local = useMemo<OrganizationSurveyDto>(
    () => (result ? orgSurveyApplyLocalPending(result) : emptyOrgSurvey(orgId, surveyName)),
    [result, orgId, surveyName],
  );

  // convert to convenient shape for consumers
  const answers = useMemo<SurveyAnswers>(() => {
    return local && local.answers
      ? Object.fromEntries(Object.values(local.answers).map((q) => [q.slug, q.answer]))
      : {};
  }, [local]);

  const flaggedForReviewSlugs = useMemo<string[]>(() => {
    return local && local.answers
      ? Object.values(local.answers)
          .filter((q) => q.flaggedForReview === true)
          .map((q) => q.slug)
      : [];
  }, [local]);

  return {
    data: local,
    answers,
    setAnswer,
    request,
    refresh,
    invalidate,
    remote: result,
    flaggedForReviewSlugs,
    mutators: {
      patchAnswer,
    },
  };
};

const queryKeySurvey = (orgId: OrganizationPublicId, surveyName: string) => [
  "survey",
  orgId,
  surveyName,
];
export const mutationKeyPatchSurveyAnswer = (orgId: OrganizationPublicId, surveyName: string) => [
  "dataMap",
  orgId,
  surveyName,
  "patchAnswer",
];

export type SurveyPatchAnswerMutation = UseMutationResult<
  OrganizationSurveyDto,
  unknown,
  PatchOrganizationSurveyAnswerRequest[],
  unknown
>;
export const usePatchSurveyAnswer = (
  orgId: OrganizationPublicId,
  surveyName: string,
): SurveyPatchAnswerMutation => {
  const qc = useQueryClient();
  const { apiError } = useApiSnackbar();

  const mutation = useMutation(
    async (updates: PatchOrganizationSurveyAnswerRequest[]) =>
      Api.orgSurvey.patchAnswersForSurvey(orgId, surveyName, updates),

    {
      mutationKey: mutationKeyPatchSurveyAnswer(orgId, surveyName),
      onMutate: (update) => {
        updateQueryData<OrganizationSurveyDto>(
          qc,
          queryKeySurvey(orgId, surveyName),
          (surveyDto) => {
            Mutators.addPendingAnswers(update, surveyDto);
          },
        );
      },

      onSuccess: (response, update) => {
        updateQueryData<OrganizationSurveyDto>(
          qc,
          queryKeySurvey(orgId, surveyName),
          (oldSurveyDto) => {
            response._local = oldSurveyDto._local;
            Mutators.removePendingAnswers(update, response);
            return response;
          },
        );
      },

      onError: (_error, update) => {
        updateQueryData<OrganizationSurveyDto>(
          qc,
          queryKeySurvey(orgId, surveyName),
          (surveyDto) => {
            Mutators.removePendingAnswers(update, surveyDto);
          },
        );
        apiError(
          "Something went wrong while we were saving the survey answer. Please try again later.",
        );
      },
    },
  );

  const running = useIsMutating({ mutationKey: mutationKeyPatchSurveyAnswer(orgId, surveyName) });
  mutation.isLoading = mutation.isLoading || running > 0;

  return mutation;
};

export const useParsedSurveyAnswers = (surveyAnswers: SurveyAnswers) =>
  useMemo<ParsedSurveyAnswers>(
    () =>
      _.transform(
        surveyAnswers,
        (res, val, key) => {
          res[key] = safeJsonParse(val);
        },
        {},
      ),
    [surveyAnswers],
  );
